import React from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../lib/i18n';

const PORTRAIT_TOP =
  'https://customer-assets.emergentagent.com/job_sacred-moments-34/artifacts/g0yxcavo_IMG_1758.JPG';
const PORTRAIT_BOTTOM =
  'https://customer-assets.emergentagent.com/job_sacred-moments-34/artifacts/figyvai1_IMG_1755.JPG';

export default function AboutPage() {
  const { t } = useI18n();
  const bio = t.about.longBio || [];
  const firstHalf = bio.slice(0, 2);
  const secondHalf = bio.slice(2);

  return (
    <main data-testid="about-page" className="bg-cream text-ink min-h-screen">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-32 md:pt-40 pb-24 md:pb-32">
        <Link
          to="/"
          data-testid="about-back-link"
          className="link-underline text-[10px] tracking-[0.4em] uppercase text-cherry hover:text-ink transition-colors mb-12 inline-block font-body"
        >
          ← {t.about.back}
        </Link>

        {/* Header — eyebrow + title, editorial */}
        <div className="mb-16 md:mb-24">
          <span className="text-[10px] tracking-[0.4em] uppercase text-cherry font-body block mb-5">
            — {t.about.eyebrow}
          </span>
          <h1
            data-testid="about-title"
            className="font-display font-medium text-ink text-5xl md:text-6xl lg:text-7xl leading-[1] max-w-4xl"
          >
            {t.about.title}
            <span className="text-cherry">.</span>
          </h1>
        </div>

        {/* Editorial first block — portrait 1 + manifesto + first half of bio */}
        <div className="grid md:grid-cols-12 gap-10 lg:gap-16 items-start">
          <figure
            className="md:col-span-5 relative"
            data-testid="about-portrait-top"
          >
            <div className="relative aspect-[4/5] overflow-hidden shadow-2xl shadow-burgundy/30">
              <img
                src={PORTRAIT_TOP}
                alt={t.about.eyebrow}
                className="absolute inset-0 w-full h-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-burgundy/8 mix-blend-multiply pointer-events-none" />
            </div>
            <figcaption className="mt-5 text-[10px] tracking-[0.4em] uppercase text-ink/45 font-body">
              {t.about.placeholder}
            </figcaption>
          </figure>

          <div className="md:col-span-7 lg:col-span-6 lg:col-start-7">
            <p
              data-testid="about-body-lead"
              className="font-display font-medium text-2xl md:text-3xl leading-snug text-cherry-deep mb-8"
            >
              {t.about.bodyA}
            </p>
            <div className="space-y-6 max-w-xl">
              {firstHalf.map((para, i) => (
                <p
                  key={i}
                  data-testid={`about-long-para-${i}`}
                  className="font-body text-ink/80 text-base md:text-[17px] leading-[1.75]"
                >
                  {para}
                </p>
              ))}
            </div>
          </div>
        </div>

        {/* Editorial second block — asymmetric offset: pull-quote + portrait 4 */}
        {secondHalf.length > 0 && (
          <div className="mt-20 md:mt-32 grid md:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="md:col-span-6 md:col-start-1 lg:col-span-5 order-2 md:order-1">
              <p className="font-la-belle text-cherry text-5xl md:text-6xl leading-none mb-8 select-none">
                &ldquo;
              </p>
              <p className="font-display font-medium text-2xl md:text-3xl leading-snug text-ink mb-8">
                {t.about.bodyB}
              </p>
              <div className="space-y-6 max-w-xl">
                {secondHalf.map((para, i) => (
                  <p
                    key={i}
                    data-testid={`about-long-para-${firstHalf.length + i}`}
                    className="font-body text-ink/80 text-base md:text-[17px] leading-[1.75]"
                  >
                    {para}
                  </p>
                ))}
              </div>
            </div>

            <figure
              className="md:col-span-6 md:col-start-7 lg:col-span-5 lg:col-start-8 order-1 md:order-2"
              data-testid="about-portrait-bottom"
            >
              <div className="relative aspect-[4/5] overflow-hidden shadow-2xl shadow-burgundy/30">
                <img
                  src={PORTRAIT_BOTTOM}
                  alt={t.about.eyebrow}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-burgundy/8 mix-blend-multiply pointer-events-none" />
              </div>
            </figure>
          </div>
        )}

        {/* Socials + CTA */}
        <div className="mt-20 md:mt-32 pt-10 border-t border-ink/15 flex flex-col md:flex-row md:items-center md:justify-between gap-10">
          <div className="flex flex-wrap items-center gap-8">
            <a
              href="https://www.linkedin.com/in/katalin-szab%C3%B3-25878777/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="social-linkedin"
              className="link-underline text-[10px] tracking-[0.4em] uppercase text-ink/70 hover:text-cherry transition-colors font-body"
            >
              LinkedIn
            </a>
            <a
              href="https://www.instagram.com/rubedo.focus/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="social-instagram"
              className="link-underline text-[10px] tracking-[0.4em] uppercase text-ink/70 hover:text-cherry transition-colors font-body"
            >
              Instagram
            </a>
            <a
              href="https://www.facebook.com/rubedofocus/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="social-facebook"
              className="link-underline text-[10px] tracking-[0.4em] uppercase text-ink/70 hover:text-cherry transition-colors font-body"
            >
              Facebook
            </a>
          </div>

          <Link
            to="/#connect"
            data-testid="about-page-connect"
            className="group inline-flex items-center gap-3 border border-burgundy text-burgundy px-6 py-3 hover:bg-burgundy hover:text-cream transition-colors duration-500 self-start md:self-auto"
          >
            <span className="text-[10px] tracking-[0.4em] uppercase font-body">{t.connect.title}</span>
            <span aria-hidden="true" className="font-display text-xl group-hover:translate-x-1 transition-transform">→</span>
          </Link>
        </div>
      </div>
    </main>
  );
}
